import { forwardRef, useId } from 'react';
import { TextField as MuiTextField } from '@mui/material';
import type {
  OutlinedTextFieldProps as MuiOutlinedTextFieldProps,
  FormHelperTextProps as MuiFormHelperTextProps,
} from '@mui/material';
import { FeedbackNotification } from '../../ui/Notification/FeedbackNotification';

export type TextFieldProps = Omit<MuiOutlinedTextFieldProps, 'variant' | 'ref'>;

/**
 * ATTENTION: only "error" prop from MuiFormHelperTextProps is supported.
 */
export const CustomHelperText = ({
  children,
  className,
  contentId,
  ...props
}: MuiFormHelperTextProps & { contentId: string }) => (
  <FeedbackNotification
    contentId={contentId}
    severity={(className && className.indexOf('error') > -1) || props.error ? 'error' : 'info'}
    variant="feedback"
    className="mt-0.5"
  >
    {children}
  </FeedbackNotification>
);

/** TextField component with label and helper text. Based on MUI TextFiel: https://mui.com/material-ui/react-text-field/ */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({ ...props }, ref) => {
  const errorMessageId = useId();
  return (
    // Set label shrink to true, to show permanently placeholder. Otherwise placeholder will be displayed only on focus.
    <MuiTextField
      {...props}
      inputProps={{ 'aria-errormessage': errorMessageId }}
      InputLabelProps={{ shrink: true }}
      variant="outlined"
      FormHelperTextProps={
        {
          component: CustomHelperText,
          error: props.error,
          contentId: errorMessageId,
        } as Partial<MuiFormHelperTextProps> & {
          contentId: string;
        }
      }
      inputRef={ref}
    />
  );
});
